import { Box } from '@mui/material';
import { useCreate } from '@refinedev/core';
import { Create, useAutocomplete } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { isEqual } from 'lodash';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';

import { ATTACKIQ_ID, API_TO_CONTENTTYPE_MAP } from '@attackiq/constants';

import AiAutocompleteInput from '../../components/inputs/AutocompleteInput/AutocompleteInput';
import { Tag } from '../../types';

const contentTypeOptions = Object.entries(API_TO_CONTENTTYPE_MAP).map(([key, value]) => ({
  api: key,
  contentType: value
}));

const TaggedItemsCreate = () => {
  const { saveButtonProps, ...formMethods } = useForm({
    refineCoreProps: {
      resource: 'tagged_items',
      action: 'create',
      redirect: false
    }
  });
  const { mutateAsync: createAsync } = useCreate();

  const { handleSubmit } = formMethods;

  const onSubmit = handleSubmit(async ({ content, content_type, tag }) => {
    content.forEach(async element => {
      await createAsync({
        resource: 'tagged_items',
        values: {
          tag: tag.id,
          object_id: element.id,
          content_type: content_type.contentType,
          company_id: ATTACKIQ_ID
        }
      });
    });
  });

  return (
    <FormProvider {...formMethods}>
      <form name="create-module-content-form" onSubmit={onSubmit}>
        <Create saveButtonProps={{ type: 'submit' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TagInput />
            <ContentTypeInput />
            <ContentInput />
          </Box>
        </Create>
      </form>
    </FormProvider>
  );
};

const TagInput = () => {
  const { autocompleteProps } = useAutocomplete<Tag>({
    resource: 'tags',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'name__icontains',
        operator: 'contains',
        value: searchText
      }
    ]
  });

  return (
    <AiAutocompleteInput
      {...autocompleteProps}
      sx={{ mt: 0.5 }}
      name="tag"
      rules={{ required: 'Tag is required' }}
      defaultValue={null}
      getOptionLabel={option => option.display_name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{
        label: 'Tag'
      }}
    />
  );
};

const ContentTypeInput = () => {
  const contentType = useWatch({ name: 'content_type', defaultValue: null });
  const { setValue } = useFormContext();

  return (
    <AiAutocompleteInput
      name="content_type"
      defaultValue={null}
      options={contentTypeOptions}
      onChange={(_event, value) => {
        if (!isEqual(value, contentType)) {
          setValue('content', []);
        }
      }}
      getOptionLabel={option => {
        if (typeof option === 'string') return option;

        return option.contentType || '';
      }}
      isOptionEqualToValue={(option, value) => option.api === value.api}
      TextFieldProps={{
        label: 'Content Type'
      }}
    />
  );
};

const ContentInput = () => {
  const contentType: (typeof contentTypeOptions)[number] = useWatch({ name: 'content_type', defaultValue: null });
  const resource = contentType ? contentType.api : null;

  return <ContentAutocomplete key={resource} resource={resource} />;
};

const ContentAutocomplete = ({ resource }: { resource: string | null }) => {
  const { autocompleteProps } = useAutocomplete({
    resource: resource ?? 'scenarios',
    pagination: { pageSize: 20 },
    debounce: 400,
    onSearch: searchText => [{ field: 'name__icontains', operator: 'contains', value: searchText }],
    queryOptions: { enabled: !!resource }
  });

  return (
    <AiAutocompleteInput
      {...(resource ? autocompleteProps : { options: [] })}
      name="content"
      defaultValue={[]}
      disabled={!resource}
      getOptionLabel={option => option.name || option.template_name || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      TextFieldProps={{ label: 'Content' }}
      multiple
    />
  );
};

export default TaggedItemsCreate;
